import type React from 'react'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Search } from 'lucide-react'
import DropdownChevron from './dropdown-chevron'

interface DropdownOption {
    label: string
    value: string
}

interface FormDropdownProps {
    label?: string
    options: DropdownOption[]
    value?: string | null
    placeholder?: string
    onSelect?: (value: string) => void
    disabled?: boolean
    className?: string
    /** Show search field and type-to-filter when the list is open. Default true. */
    searchable?: boolean
}

const FormDropdown: React.FC<FormDropdownProps> = ({
    label,
    options,
    value,
    placeholder,
    onSelect,
    disabled = false,
    className = '',
    searchable = true,
}) => {
    const [isOpen, setIsOpen] = useState(false)
    const [searchQuery, setSearchQuery] = useState('')
    const dropdownRef = useRef<HTMLDivElement>(null)
    const searchInputRef = useRef<HTMLInputElement>(null)

    const selectedOption = useMemo(
        () => options.find((option) => option.value === (value ?? '')) ?? null,
        [options, value]
    )

    const closeDropdown = useCallback(() => {
        setIsOpen(false)
        setSearchQuery('')
    }, [])

    const filteredOptions = useMemo(() => {
        if (!searchable) return options
        const query = searchQuery.trim().toLowerCase()
        if (!query) return options
        return options.filter((option) => option.label.toLowerCase().includes(query))
    }, [options, searchQuery, searchable])

    useEffect(() => {
        const handleOutsideClick = (event: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                closeDropdown()
            }
        }

        document.addEventListener('mousedown', handleOutsideClick)
        return () => document.removeEventListener('mousedown', handleOutsideClick)
    }, [closeDropdown])

    useEffect(() => {
        if (!isOpen || !searchable) return

        const frameId = window.requestAnimationFrame(() => {
            searchInputRef.current?.focus()
        })

        return () => window.cancelAnimationFrame(frameId)
    }, [isOpen, searchable])

    useEffect(() => {
        if (!isOpen || disabled || !searchable) return

        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Escape') {
                event.preventDefault()
                closeDropdown()
                return
            }

            const target = event.target as HTMLElement
            if (target === searchInputRef.current) return
            if (
                (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA') &&
                !dropdownRef.current?.contains(target)
            ) {
                return
            }

            if (
                event.key.length === 1 &&
                !event.ctrlKey &&
                !event.metaKey &&
                !event.altKey
            ) {
                event.preventDefault()
                searchInputRef.current?.focus()
                setSearchQuery((prev) => prev + event.key)
            }
        }

        document.addEventListener('keydown', handleKeyDown)
        return () => document.removeEventListener('keydown', handleKeyDown)
    }, [isOpen, disabled, searchable, closeDropdown])

    const handleSelect = (selectedValue: string) => {
        closeDropdown()
        onSelect?.(selectedValue)
    }

    return (
        <div className={className}>
            {label && <label className="block text-sm font-medium text-[#000000] mb-2">{label}</label>}
            <div className="relative" ref={dropdownRef}>
                <button
                    type="button"
                    onClick={() => {
                        if (disabled) return
                        if (isOpen) closeDropdown()
                        else setIsOpen(true)
                    }}
                    disabled={disabled}
                    className={`w-full px-4 py-2 rounded-md bg-[#F9F9F9] text-left flex justify-between items-center transition-colors focus:outline-none focus:ring-2 focus:ring-lime-400 ${selectedOption ? 'text-gray-800' : 'text-gray-400'} ${disabled ? 'cursor-not-allowed opacity-60' : 'cursor-pointer'}`}
                >
                    <span>
                        {selectedOption?.label || placeholder || 'Select an option'}
                    </span>
                    <DropdownChevron isOpen={isOpen} disabled={disabled} />
                </button>
                {isOpen && !disabled && (
                    <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-md shadow-lg z-50 overflow-hidden">
                        {searchable && (
                            <div
                                className="sticky top-0 z-10 border-b border-gray-200 bg-white p-2"
                                onMouseDown={(event) => event.stopPropagation()}
                            >
                                <div className="relative">
                                    <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                                    <input
                                        ref={searchInputRef}
                                        type="text"
                                        value={searchQuery}
                                        onChange={(event) => setSearchQuery(event.target.value)}
                                        onKeyDown={(event) => {
                                            if (event.key === 'Enter' && filteredOptions.length > 0) {
                                                event.preventDefault()
                                                handleSelect(filteredOptions[0].value)
                                            }
                                        }}
                                        placeholder="Type to search..."
                                        className="w-full rounded-md border border-gray-200 bg-[#F9F9F9] py-2 pl-9 pr-3 text-sm text-gray-800 placeholder:text-gray-400 focus:border-lime-400 focus:outline-none focus:ring-2 focus:ring-lime-400"
                                        aria-label="Search options"
                                    />
                                </div>
                            </div>
                        )}

                        <div className="max-h-64 overflow-y-auto">
                            {filteredOptions.length === 0 ? (
                                <p className="px-4 py-3 text-sm text-gray-500">No results found</p>
                            ) : (
                                filteredOptions.map((option) => (
                                    <button
                                        type="button"
                                        key={option.value}
                                        onClick={() => handleSelect(option.value)}
                                        className="w-full text-left px-4 py-3 hover:bg-[#ABD405] transition-colors text-gray-700 border-b border-gray-100 last:border-b-0"
                                    >
                                        {option.label}
                                    </button>
                                ))
                            )}
                        </div>
                    </div>
                )}
            </div>
        </div>
    )
}

export default FormDropdown
